import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment'
import { useIntl } from 'react-intl'
import { replace } from 'lib/routing/appRouting'
import ExperienceSearchPageFilters from './ExperienceSearchPageFilters'
import FilterPanelFilterGroup from 'components/Common/FilterPanel/FilterPanelFilterGroup'
import ExperiencesSortByOptions from './ExperiencesSortByOptions'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import { convertSearchFiltersToUrl } from 'components/Experiences/hooks/useExperiencesSearchFilters'
import useTrackExperienceSearchFilter from 'components/Experiences/hooks/useTrackExperienceSearchFilter'
import { useAppDispatch } from 'hooks/reduxHooks'
import useQueryParams from 'hooks/useQueryParams'
import ExperienceSearchFilterDraftContext from 'contexts/Experience/ExperienceSearchFilterDraftContext'
import useExperiencesSearchDateDispatch from '../hooks/useExperiencesSearchDateDispatch'

interface Props {
  open?: boolean;
  onClose?: () => void;
  place?: App.Place;
  listIds?: Array<string>;
}

function ExperienceSearchPageFiltersSidebar(props: Props) {
  const { open, onClose, place, listIds } = props
  const intl = useIntl()
  const trackSearchFilter = useTrackExperienceSearchFilter()
  const dispatch = useAppDispatch()
  const queryParams = useQueryParams()
  const [draftQueryParams, setDraftQueryParams] = useState(queryParams)
  const [resetKey, setResetKey] = useState(0)
  const previousQueryParams = useRef(queryParams)
  const wasOpen = useRef(false)
  const { selectAnytime, selectDayRange } = useExperiencesSearchDateDispatch()
  const draftContext = useMemo(() => ({
    queryParams: draftQueryParams,
    setQueryParams: setDraftQueryParams,
  }), [draftQueryParams])

  useEffect(() => {
    if (open) {
      if (!wasOpen.current) {
        setDraftQueryParams(new URLSearchParams(queryParams))
        setResetKey(key => key + 1)
      } else {
        const previous = previousQueryParams.current
        setDraftQueryParams(current => {
          const next = new URLSearchParams(queryParams)
          // Preserve draft edits, including removals, over unrelated URL updates.
          const keys = new Set([...previous.keys(), ...current.keys()])
          for (const key of keys) {
            const values = current.getAll(key)
            if (JSON.stringify(values) !== JSON.stringify(previous.getAll(key))) {
              next.delete(key)
              values.forEach(value => next.append(key, value))
            }
          }
          return next
        })
      }
    }
    previousQueryParams.current = queryParams
    wasOpen.current = !!open
  }, [open, queryParams])

  const handleApply = useCallback(() => {
    trackSearchFilter({
      label: 'All filters',
      type: 'filters',
      value: 'applied',
    })
    const checkIn = draftQueryParams.get('checkIn')
    const checkOut = draftQueryParams.get('checkOut')
    if (checkIn !== queryParams.get('checkIn') || checkOut !== queryParams.get('checkOut')) {
      if (checkIn) selectDayRange(moment(checkIn), moment(checkOut ?? checkIn))
      else selectAnytime()
    }
    if (draftQueryParams.toString() !== queryParams.toString()) {
      dispatch(replace({ search: draftQueryParams.toString() }))
    }
    onClose?.()
  }, [onClose, trackSearchFilter, draftQueryParams, queryParams, dispatch, selectAnytime, selectDayRange])

  const onClearAll = useCallback(() => {
    trackSearchFilter({
      label: 'All filters',
      type: 'filters',
      value: 'cleared',
    })
    setDraftQueryParams(current => convertSearchFiltersToUrl({}, current))
    // Remount the controls to cancel pending price timers and reset local inputs.
    setResetKey(key => key + 1)
  }, [trackSearchFilter])

  return (
    <ModalBase mode="drawer" isOpen={open} onClose={onClose}>
      <ModalHeader
        title={intl.formatMessage({
          id: '-sHD19_YfrtV2KBci1xt',
          defaultMessage: 'Sort & filter',
          description:
            'Title for the sort and filter sidebar modal in experience search',
        })}
        onCloseButtonClick={onClose}
      />
      <ExperienceSearchFilterDraftContext.Provider key={resetKey} value={draftContext}>
        <ModalBody>
          <ModalContent>
            <FilterPanelFilterGroup
              title={intl.formatMessage({
                id: 'iD2zuFMw3T1Y-PseOsOg',
                defaultMessage: 'Sort by',
                description:
                  'Title for the sort options group in experience search filter sidebar',
              })}
            >
              <ExperiencesSortByOptions />
            </FilterPanelFilterGroup>
            <ExperienceSearchPageFilters
              place={place}
              listIds={listIds}
            />
          </ModalContent>
        </ModalBody>
        <ModalFooter
          primaryActionProps={{
            children: intl.formatMessage({
              id: 'XGAxBfjicX0x40wpm7Xt',
              defaultMessage: 'Apply',
              description:
                'Button text to apply sort and filter selections in experience search',
            }),
            onClick: handleApply,
            style: { flexGrow: 2 },
          }}
          secondaryActionProps={{
            children: intl.formatMessage({
              id: 'MHb8lQ7Liy1ixnu8EKVa',
              defaultMessage: 'Clear all',
              description:
                'Action button label to clear all applied flight filters',
            }),
            onClick: onClearAll,
            style: { flexGrow: 0 },
          }}
        />
      </ExperienceSearchFilterDraftContext.Provider>
    </ModalBase>
  )
}

export default ExperienceSearchPageFiltersSidebar
